import React, {
	createContext,
	useCallback,
	useContext,
	useMemo,
	useState,
} from 'react';

interface PopoverProviderProps {
	onClose?: () => void;
	resetShouldCloseOnNavigation: (shouldClose?: boolean) => void;
	resetShouldCloseOnOutsideClick: (shouldClose?: boolean) => void;
	setShouldCloseOnNavigation: (shouldClose: boolean) => void;
	setShouldCloseOnOutsideClick: (shouldClose: boolean) => void;
	shouldCloseOnNavigation: boolean;
	shouldCloseOnOutsideClick: boolean;
}

const PopoverContext = createContext<PopoverProviderProps | undefined>(
	undefined,
);

interface Props {
	children: React.ReactNode;

	/** Callback function when the popover should close. */
	onClose?: () => void;

	/** If the popover should close when the URL is updated. */
	shouldCloseOnNavigation?: boolean;

	/** If the popover should close when clicking on the background outside it. */
	shouldCloseOnOutsideClick?: boolean;
}

export default function PopoverContextProvider({
	children,
	onClose,
	shouldCloseOnNavigation: shouldCloseOnNavigationProp = true,
	shouldCloseOnOutsideClick: shouldCloseOnOutsideClickProp = true,
}: Props) {
	const [shouldCloseOnNavigation, setShouldCloseOnNavigation] = useState(
		shouldCloseOnNavigationProp,
	);
	const [shouldCloseOnOutsideClick, setShouldCloseOnOutsideClick] = useState(
		shouldCloseOnOutsideClickProp,
	);
	const resetShouldCloseOnNavigation = useCallback(
		(shouldClose) => {
			setShouldCloseOnNavigation(shouldClose ?? shouldCloseOnNavigationProp);
		},
		[shouldCloseOnNavigationProp],
	);
	const resetShouldCloseOnOutsideClick = useCallback(
		(shouldClose) => {
			setShouldCloseOnOutsideClick(
				shouldClose ?? shouldCloseOnOutsideClickProp,
			);
		},
		[shouldCloseOnOutsideClickProp],
	);

	const value = useMemo(
		() => ({
			shouldCloseOnNavigation,
			setShouldCloseOnNavigation,
			shouldCloseOnOutsideClick,
			setShouldCloseOnOutsideClick,
			onClose,
			resetShouldCloseOnNavigation,
			resetShouldCloseOnOutsideClick,
		}),
		[
			shouldCloseOnNavigation,
			shouldCloseOnOutsideClick,
			onClose,
			resetShouldCloseOnNavigation,
			resetShouldCloseOnOutsideClick,
		],
	);

	return (
		<PopoverContext.Provider value={value}>{children}</PopoverContext.Provider>
	);
}
PopoverContextProvider.displayName = 'PopoverContextProvider';

export function usePopoverContext() {
	const context = useContext(PopoverContext);
	if (!context) {
		throw new Error('usePopoverContext must be used within a PopoverProvider');
	}
	return context;
}
